import React, { useState } from "react";
import { api } from "../lib/api";
import { Music2, ExternalLink, Mail, Check, Play, Youtube } from "lucide-react";
import { toast } from "sonner";

/**
 * MUSIC page — showcases Robert's original tracks with platform-agnostic
 * "PLAY ON..." buttons that deep-link to Spotify / Apple Music / YouTube Music.
 *
 * When Robert supplies direct track URLs (spotify:track:... etc), swap the
 * `search` strings below for the `direct` URLs and the buttons still work.
 */

// Robert's artist name as it appears on streaming services.
// Update this once he confirms the exact DistroKid artist handle.
const ARTIST_NAME = "EVERYTHANG REDNECK";

// Set direct=... on any track once we have the real URL from Spotify/Apple/YT.
// Falls back to a search link with title + artist name so buttons always work.
const TRACKS = [
  {
    slug: "redneck-rich",
    title: "Redneck Rich",
    blurb: "An anthem for folks who count their wealth in sunsets, dirt roads, and cold ones.",
  },
  {
    slug: "together-we-rise",
    title: "Together We Rise",
    blurb: "Unity, grit, and neighbors on the front porch when it matters most.",
  },
  {
    slug: "crimson-conquerors",
    title: "Crimson Conquerors",
    blurb: "Stand tall, brothers and sisters — a rallying cry for the ones who refuse to break.",
  },
  {
    slug: "redneck-rebirth",
    title: "Redneck Rebirth",
    blurb: "A softer side — the story of a soul walking away from city lights toward dirt roads, fireflies, and gold-painted sunsets.",
  },
  {
    slug: "warriors-of-the-sunrise",
    title: "Warriors of the Sunrise",
    blurb: "Sunrise as the battle line — every day earned, every dawn hard-fought.",
  },
  {
    slug: "united-we-ride",
    title: "United We Ride",
    blurb: "Bikers, brothers, side by side — through the fire and the rain, freedom calls.",
  },
  {
    slug: "crimson-brotherhood",
    title: "Crimson Brotherhood",
    blurb: "Blood-bound loyalty, iron-forged spirit — the ones you ride or die with.",
  },
  {
    slug: "redneck-revolution",
    title: "Redneck Revolution",
    blurb: "The people rise. The dirt-road faithful take back what's theirs.",
  },
];

const buildLinks = (track) => {
  const q = encodeURIComponent(`${track.title} ${ARTIST_NAME}`);
  return {
    spotify: track.spotify || `https://open.spotify.com/search/${q}`,
    apple: track.apple || `https://music.apple.com/us/search?term=${q}`,
    youtube: track.youtube || `https://music.youtube.com/search?q=${q}`,
  };
};

function PlatformButton({ href, label, icon: Icon, colorHex }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`play-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="group flex items-center justify-between px-4 py-3 border-2 border-[#2C332A] hover:border-[#DD5422] transition-colors bg-[#0A0E0B]"
      style={{ borderColor: colorHex ? `${colorHex}40` : undefined }}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" style={{ color: colorHex }} />
        <span className="font-anton uppercase tracking-widest text-xs text-[#E5E5E5]">{label}</span>
      </div>
      <ExternalLink className="w-3.5 h-3.5 text-[#8D9688] group-hover:text-[#DD5422] transition-colors" />
    </a>
  );
}

function TrackCard({ track, index }) {
  const links = buildLinks(track);
  return (
    <article
      data-testid={`track-${track.slug}`}
      className="group bg-[#151A14] border-2 border-[#2C332A] hover:border-[#DD5422] transition-colors fade-up flex flex-col"
      style={{ animationDelay: `${index * 0.06}s` }}
    >
      <div className="relative aspect-square bg-gradient-to-br from-[#DD5422] via-[#8B2E12] to-[#0A0E0B] overflow-hidden">
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
          <Music2 className="w-16 h-16 text-[#0A0E0B]/40 mb-4" strokeWidth={1.5} />
          <div className="font-anton text-3xl uppercase leading-none text-[#0A0E0B] drop-shadow-lg">
            {track.title}
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#0A0E0B]/70 font-anton">
            {ARTIST_NAME}
          </div>
        </div>
        <div className="absolute top-3 right-3 bg-[#0A0E0B] text-[#DD5422] px-2 py-1 font-anton text-xs uppercase tracking-widest">
          Track {String(index + 1).padStart(2, "0")}
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-anton text-2xl uppercase tracking-tight leading-none">{track.title}</h3>
        <p className="text-[#8D9688] text-sm mt-2 flex-1">{track.blurb}</p>
        <div className="mt-5 space-y-2">
          <PlatformButton href={links.spotify} label="Play on Spotify" icon={Play} colorHex="#1DB954" />
          <PlatformButton href={links.apple} label="Apple Music" icon={Music2} colorHex="#FA57C1" />
          <PlatformButton href={links.youtube} label="YouTube Music" icon={Youtube} colorHex="#FF0000" />
        </div>
      </div>
    </article>
  );
}

function ReleaseSignup() {
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Drop a real email, friend.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/waitlist", { slug: "new-song-releases", email });
      setJoined(true);
      toast.success("You're on the list — we'll holler when a new track drops.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join the list");
    } finally {
      setSubmitting(false);
    }
  };

  if (joined) {
    return (
      <div data-testid="music-signup-joined" className="border-2 border-[#4CAF50] bg-[#4CAF50]/10 p-4 flex items-center gap-3 max-w-lg">
        <Check className="w-5 h-5 text-[#4CAF50] shrink-0" />
        <div className="font-anton uppercase tracking-widest text-sm text-[#4CAF50]">
          You&apos;re on the list — first to know when a new track drops.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-lg" data-testid="music-signup-form">
      <div className="relative flex-1">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D9688]" />
        <input
          type="email"
          required
          data-testid="music-signup-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="w-full pl-9 pr-3 py-3 bg-[#0A0E0B] border-2 border-[#2C332A] text-[#E5E5E5] focus:border-[#DD5422] outline-none uppercase tracking-widest text-xs font-anton"
        />
      </div>
      <button
        type="submit"
        data-testid="music-signup-submit"
        disabled={submitting}
        className="px-6 py-3 bg-[#DD5422] text-[#0A0E0B] font-anton uppercase tracking-widest text-sm hover:bg-[#FF6B35] disabled:opacity-50 border-2 border-[#DD5422]"
      >
        {submitting ? "..." : "Notify Me"}
      </button>
    </form>
  );
}

export default function Music() {
  return (
    <div className="min-h-screen" data-testid="music-page">
      {/* Hero with album cover */}
      <section className="relative overflow-hidden border-b-2 border-[#2C332A]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#DD5422]/20 via-[#0A0E0B] to-[#0A0E0B]" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-3 flex items-center gap-2">
                <Music2 className="w-4 h-4" /> / The Album
              </div>
              <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight leading-none">
                Redneck <br />
                <span className="text-[#DD5422]">Revolution.</span>
              </h1>
              <p className="text-[#8D9688] mt-6 max-w-xl">
                8 original tracks from EVERYTHANG REDNECK. Unity, grit, and the people this country was built by. Stream on your platform of choice — Spotify, Apple Music, YouTube Music.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <div className="border-2 border-[#DD5422] px-4 py-2 font-anton uppercase tracking-widest text-xs text-[#DD5422]" data-testid="album-badge">
                  8 Tracks · New Release
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="/api/designs/album-cover-everythang-redneck.png"
                alt="Redneck Revolution — album cover, aurora borealis photographed by the artist in his own front yard"
                data-testid="album-cover-img"
                className="w-full max-w-md mx-auto border-4 border-[#DD5422] shadow-2xl"
              />
              <div className="text-center mt-3 text-[10px] uppercase tracking-widest text-[#8D9688] font-anton">
                Cover photo · Aurora borealis, shot by the artist in his own front yard
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tracks */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="tracks-section">
        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
          <h2 className="font-anton text-3xl md:text-4xl uppercase tracking-tight">The Tracks</h2>
          <div className="text-xs uppercase tracking-widest text-[#8D9688]">
            {TRACKS.length} tracks · more comin&apos;
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TRACKS.map((t, i) => <TrackCard key={t.slug} track={t} index={i} />)}
        </div>
      </section>

      {/* Follow + Notify */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="bg-[#151A14] border-2 border-[#DD5422] p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-xs mb-2">
                / Never Miss A Drop
              </div>
              <h3 className="font-anton text-3xl md:text-4xl uppercase tracking-tight leading-none">
                Get The Next Track Before Anybody Else.
              </h3>
              <p className="text-[#8D9688] mt-3 max-w-md">
                One email. No spam. Just a holler when a new track hits streaming.
              </p>
            </div>
            <div>
              <ReleaseSignup />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
